import { values, first, get, size, defer, pick, has } from 'lodash';
import React, { PureComponent, ReactNode } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { BBCommonProps, BBUiConfig, ComponentTypes, EditorMode } from 'types/story';
import { ClientReducerState } from 'client/reducers';
import { clientLog, isCMD } from 'common/utils/helpers';
import { CARD_ROOT_ELEMENT_INDEX as CREI, COMPONENT_TYPE } from 'common/constants';
import { SELECTION_TYPES, SELECTION_STAGE, isUserAction } from 'client/components/common/SelectionHint/utils';
import Points from 'client/components/common/SelectionHint/Draggable/Points';
import Store from 'client/store';
import { SelectionHintController } from './SelectionHintController';
import { SelectionHintEvent } from './SelectionHintEvent';
import { Context } from './SelectionHintContext';
import { SelectionSingleType } from './types';

const log = clientLog.extend('SelectionHintProvider');

const { Provider } = Context;

const contentModeForbiddenNodes = pick(COMPONENT_TYPE, [
	'LOTTIE',
	'SHAPE',
	'RESULT_SHAPE',
	'RESULT_TEXT',
	'SMS_LOGIN',
	'GOOGLE_LOGIN',
]);

// can a component be a container for other components
const isAllowedContentModeNode = (uiConfig: BBUiConfig, type: ComponentTypes) =>
	!uiConfig.editorProps.expandable && !has(contentModeForbiddenNodes, type);

type Props = {
	isEditor: boolean;
	children: ReactNode;
} & ConnectedProps<typeof connector>;

class SelectionHintProvider extends PureComponent<Props> {
	currentId = '';

	currentStage: (typeof SELECTION_STAGE)[keyof typeof SELECTION_STAGE] = SELECTION_STAGE.OUTER;

	get treeOptions() {
		if (!this.props.isEditor) {
			return null;
		}

		// these props are treated by BuildingBlockEnhancer.js, who receives them from traverseTree function
		return {
			onMouseDown: this.onEditableMouseDown,
			onFocus: this.onFocus,
			onMouseEnter: this.onMouseEnter,
			onMouseLeave: this.onMouseLeave,
			onMouseDownCapture: this.onMouseDownCapture,
			onDoubleClickCapture: this.onDoubleClickCapture,
		};
	}

	get providerValue() {
		return {
			treeOptions: this.treeOptions,
		};
	}

	get isContentEditor() {
		return this.props.editorMode === EditorMode.CONTENT;
	}

	onFocus = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>) => {
		log('Focus');
		e.stopPropagation();

		new SelectionHintEvent({
			options: {
				detail: {
					trigger: SelectionHintController.FOCUS,
					selectionType: SELECTION_TYPES.clicked,
					originalEvent: e,
					componentProps: props,
				},
			},
		}).dispatch();
	};

	onMouseEnter = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>) => {
		const checkResult = check(props, e, this.currentStage);

		if (!checkResult) {
			return;
		}

		const { isTopLevel, isSibling, isFirstLvlChild, isUnselectable, isCrossLayerSelectionAllowed } = checkResult;
		// log(`onMouseEnter: ${props.type} %O`, props);

		if (isUnselectable) {
			return;
		}

		const isAllow = this.isContentEditor
			? isAllowedContentModeNode(props.uiConfig, props.type)
			: (isFirstLvlChild && this.currentStage === SELECTION_STAGE.INNER) ||
			  isSibling ||
			  isTopLevel ||
			  isCrossLayerSelectionAllowed ||
			  isCMD(e);

		if (isAllow) {
			// log(`onMouseEnter accepted: ${e.currentTarget.dataset.bb} at ${props.type}`);

			// stop bubbling
			e.stopPropagation();

			// hover
			new SelectionHintEvent({
				options: {
					detail: {
						trigger: SelectionHintController.SELECT,
						selectionType: SELECTION_TYPES.hovered,
						originalEvent: e,
						componentProps: props,
					},
				},
			}).dispatch();
		}
	};

	onMouseLeave = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>, force?: boolean) => {
		const hovered = first(values(get(getSelection(), SELECTION_TYPES.hovered)));
		const isHovered =
			hovered && hovered.editableModeProps.nodeProps?.['data-path'] === e.currentTarget.dataset.path;

		if (isHovered || force) {
			// stop bubbling
			e.stopPropagation();

			// clear hover
			new SelectionHintEvent({
				options: {
					detail: {
						trigger: SelectionHintController.DESELECT,
						selectionType: SELECTION_TYPES.hovered,
						originalEvent: e,
						componentProps: null,
					},
				},
			}).dispatch();
		}
	};

	onEditableMouseDown = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>) => {
		const { shiftKey } = e;
		const { uiConfig, type } = props;
		const selected = get(getSelection(), SELECTION_TYPES.clicked);
		const isAlreadySelected = !!selected[uiConfig.nodeProps.id];
		const isSimpleSelfClick = isAlreadySelected && !shiftKey;

		const isAllow = this.isContentEditor
			? // select only allowed elements or let to select container programmatically
			  isAllowedContentModeNode(uiConfig, type) || !isUserAction(e)
			: // allow with CMD, but multi-select only programmatically (from Layers for example)
			  isCMD(e) && (isUserAction(e) ? !shiftKey : true);

		if (!isAllow) {
			log(`onEditableMouseDown return: ${e.currentTarget.dataset.bb} — ${type}`);
			return;
		}

		log(`onEditableMouseDown success: ${e.currentTarget.dataset.bb} — ${type}`);

		this.currentId = uiConfig.nodeProps.id;
		this.currentStage = SELECTION_STAGE.OUTER;

		// stop bubbling
		e.stopPropagation();

		if (!isSimpleSelfClick) {
			// select
			new SelectionHintEvent({
				options: {
					detail: {
						trigger: SelectionHintController.SELECT,
						selectionType: SELECTION_TYPES.clicked,
						originalEvent: e,
						componentProps: props,
					},
				},
			}).dispatch();
		}
	};

	// only children can be selected with a double click
	onDoubleClickCapture = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>) => {
		const checkResult = check(props, e, this.currentStage);

		if (!checkResult || e.shiftKey) {
			return;
		}

		const { isFirstLvlChild, isCrossLayerSelectionAllowed } = checkResult;

		log('dbclick capture', { props, stage: this.currentStage });

		if (
			!this.isContentEditor &&
			props.uiConfig.editorProps.expandable && // is container element
			this.currentId === props.uiConfig.nodeProps.id &&
			this.currentStage !== SELECTION_STAGE.INNER &&
			size(getSelection()[SELECTION_TYPES.clicked]) === 1
		) {
			log('dbclick capture: set STAGE.INNER');
			this.currentStage = SELECTION_STAGE.INNER;

			// store stage for a clicked node
			new SelectionHintEvent({
				options: {
					detail: {
						trigger: SelectionHintController.SELECT,
						selectionType: SELECTION_TYPES.clicked,
						originalEvent: e,
						componentProps: props,
						stage: this.currentStage,
					},
				},
			}).dispatch();
		}

		if ((isFirstLvlChild && this.currentStage === SELECTION_STAGE.INNER) || isCrossLayerSelectionAllowed) {
			log('dbclick capture: dispatch mouseenter');
			this.onMouseEnter(e, props); // highlight child
		}
	};

	// only top level or siblings can be selected with a click capturing
	onMouseDownCapture = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>) => {
		const checkResult = check(props, e, this.currentStage);

		if (!checkResult) {
			return;
		}

		const {
			selectedPath,
			capturePath,
			isTopLevel,
			isSibling,
			isFirstLvlChild,
			isUnselectable,
			isCrossLayerSelectionAllowed,
			target,
		} = checkResult;
		const _isUserAction = isUserAction(e);
		const { currentTarget } = e;

		// allow to select unselectable only programmatically from LayersTree
		if (isUnselectable && _isUserAction) {
			return;
		}

		const { shiftKey } = e;
		const selected = get(getSelection(), SELECTION_TYPES.clicked);
		const isAlreadySelected = !!selected[props.uiConfig.nodeProps.id];
		const isMultipleSelected = size(selected) > 1;
		// block multi-select with card bb
		const isCardMultiSelect = shiftKey && (props.type === 'CARD' || values(selected).some(s => s.type === 'CARD'));
		// is click on already selected target. is not deselect of multi-selection
		const isSimpleSelfClick = isAlreadySelected && !shiftKey;

		if (isCardMultiSelect || isSimpleSelfClick || isCMD(e) || this.isContentEditor) {
			return; // skip capture and dive to clicked target (onEditableMouseDown)
		}

		const hasDeeperChildToSelect = () =>
			target !== currentTarget && !isLocked(target) && hasLockedParent(target, currentTarget);

		if (
			isCrossLayerSelectionAllowed ||
			isSibling ||
			(isTopLevel && !hasDeeperChildToSelect()) ||
			(isTopLevel && !isLocked(currentTarget)) ||
			(isFirstLvlChild && this.currentStage === SELECTION_STAGE.INNER)
		) {
			log(`onMouseDownCapture: ${currentTarget.dataset.bb} — ${props.type}`, { selectedPath, capturePath });

			this.currentId = props.uiConfig.nodeProps.id;
			this.currentStage = SELECTION_STAGE.OUTER;

			this.handleSelect(e, props);

			if (!_isUserAction || shiftKey || isMultipleSelected) {
				log('onMouseDownCapture: return', { _isUserAction, shiftKey, isMultipleSelected });
				return;
			}

			defer(() => {
				/*
				 1. this.onMouseDownCapture: store selected node data
				 2. Points: render DnD API for a selected node (+ add event listener to document for a free drag node)
				 3. deferred dispatch event for a created listener in a 2nd step to start node dragging immediately
				 */
				log('onMouseDownCapture: defer distpach');
				const node = document.getElementById(props.uiConfig.nodeProps.id);
				node?.dispatchEvent?.(new MouseEvent(Points.FREE_DRAG_EVENT, e));
			});
		}
	};

	handleSelect = (e, props: WithRequired<BBCommonProps, 'editableModeProps'>) => {
		// stop capture
		e.stopPropagation();

		// select
		new SelectionHintEvent({
			options: {
				detail: {
					trigger: SelectionHintController.SELECT,
					selectionType: SELECTION_TYPES.clicked,
					originalEvent: e,
					componentProps: props,
					stage: this.currentStage,
				},
			},
		}).dispatch();

		// clear hover
		this.onMouseLeave(e, props, true);
	};

	render() {
		return <Provider value={this.providerValue}>{this.props.children}</Provider>;
	}
}

function getSelection() {
	return (Store.getState() as ClientReducerState).editor.selection;
}

function check(
	props: BBCommonProps,
	e: React.MouseEvent<any>,
	stage: (typeof SELECTION_STAGE)[keyof typeof SELECTION_STAGE]
) {
	const selected = first(values(get(getSelection(), SELECTION_TYPES.clicked)));
	const target = getBB(e.target as HTMLElement);

	if (!selected || !target) {
		return false;
	}

	const selectedPath = selected.editableModeProps.nodeProps?.['data-path'] ?? '';
	const selectedPathA = selectedPath.split('.');

	const capturePath = props.editableModeProps?.nodeProps?.['data-path'] ?? '';
	const capturePathA = capturePath.split('.');

	const capturedNodeRef = document.getElementById(props.uiConfig.nodeProps.id);
	const selectedNodeRef = document.getElementById(selected.uiConfig.nodeProps.id);
	const isTopLevelParent = Boolean(capturedNodeRef?.contains(selectedNodeRef));

	const isTopLevel =
		(capturePathA.length === 3 || capturePath === `${CREI[COMPONENT_TYPE.CARD]}`) && !isTopLevelParent;
	const isFirstLvlChild =
		capturePath.startsWith(selectedPath) && capturePathA.slice(0, -2).join('.') === selectedPath;
	const isSibling =
		capturePathA.slice(0, -1).join('.') === selectedPathA.slice(0, -1).join('.') && capturePath !== selectedPath;

	const isUnselectable = !props.uiConfig.editorProps.selectable;

	let isCrossLayerSelectionAllowed =
		!isTopLevel && !isTopLevelParent && !isSibling && e.currentTarget.dataset.lock !== 'true';

	if (isCrossLayerSelectionAllowed) {
		const parents = getSelectableParents(e.currentTarget, selected);
		isCrossLayerSelectionAllowed = parents.length > 0 && parents.every(el => el.dataset.lock === 'true');

		if (isCrossLayerSelectionAllowed && capturePath.startsWith(selectedPath) && stage !== SELECTION_STAGE.INNER) {
			isCrossLayerSelectionAllowed = false;
		}
	}

	return {
		isTopLevel,
		isSibling,
		isFirstLvlChild,
		isCrossLayerSelectionAllowed,
		selected,
		selectedPath,
		selectedPathA,
		capturePath,
		capturePathA,
		isUnselectable,
		target,
	};
}

function getBB(node: HTMLElement): HTMLElement | null {
	return node.hasAttribute('data-bb') ? node : node.closest('[data-bb]');
}

function isLocked(node: HTMLElement) {
	return node.dataset.lock === 'true';
}

function hasLockedParent(node: HTMLElement, lastNode: HTMLElement): boolean {
	if (node === lastNode) return false;

	if (isLocked(node.parentNode as HTMLElement)) return true;

	return hasLockedParent(node.parentNode as HTMLElement, lastNode);
}

function getSelectableParents(node, selected: SelectionSingleType) {
	let el = node;
	const parents: HTMLElement[] = [];

	while (
		el.parentNode &&
		el.parentNode.dataset.path?.split('.').length > 1 &&
		el.parentNode.id !== selected.uiConfig.nodeProps.id
	) {
		el = el.parentNode;
		parents.push(el);
	}
	return parents;
}

const connector = connect((state: ClientReducerState) => ({ editorMode: state.editor.editorMode }));

export default connector(SelectionHintProvider);
